import { useState, useEffect, useCallback } from 'react';
import { supabase, useAuth } from '@mondivago/supabase';
import { uploadOptimizedImage } from '@/shared/lib/storage';
import type { PartnerAccount, UsePartnerAccountResult } from '@mondivago/types';

export type { PartnerAccount };
export type { UsePartnerAccountResult } from '@mondivago/types';

/**
 * Loads and updates the **current user's own** `partner_workers` record.
 *
 * This is the row that backs the partner "Account Settings" page — it holds
 * the agent's display name, contact email and phone.
 */
export function usePartnerAccount(): UsePartnerAccountResult {
  const { user } = useAuth();
  const [worker, setWorker] = useState<PartnerAccount | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const userId = user?.id;

  const fetchWorker = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      setWorker(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error: dbError } = await supabase
        .from('partner_workers')
        .select('id, user_id, partner_id, first_name, last_name, email, phone, photo_url, role')
        .eq('user_id', userId)
        .maybeSingle();

      if (dbError) throw dbError;

      setWorker((data as PartnerAccount) ?? null);
    } catch {
      setError('Error loading your account. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const updateAccount = useCallback(
    async (updates: Partial<Pick<PartnerAccount, 'first_name' | 'last_name' | 'phone' | 'photo_url'>>) => {
      if (!userId) return { success: false, error: 'Not authenticated' };

      setSaving(true);
      setError(null);

      try {
        const { data, error: dbError } = await supabase
          .from('partner_workers')
          .update({ ...updates, updated_at: new Date().toISOString() })
          .eq('user_id', userId)
          .select('id, user_id, partner_id, first_name, last_name, email, phone, photo_url, role')
          .maybeSingle();

        if (dbError) throw dbError;

        if (data) setWorker(data as PartnerAccount);
        return { success: true };
      } catch {
        const message = 'Error saving your account. Please try again.';
        setError(message);
        return { success: false, error: message };
      } finally {
        setSaving(false);
      }
    },
    [userId]
  );

  const uploadPhoto = useCallback(
    async (file: File) => {
      if (!userId) return { success: false, error: 'Not authenticated' };

      setSaving(true);
      setError(null);

      try {
        const url = await uploadOptimizedImage({
          file,
          bucket: 'users',
          folder: `partners/${userId}`,
          prefix: 'avatar',
        });

        const res = await updateAccount({ photo_url: url });
        if (!res.success) throw new Error(res.error);

        return { success: true, url };
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Error uploading photo. Please try again.';
        setError(message);
        return { success: false, error: message };
      } finally {
        setSaving(false);
      }
    },
    [userId, updateAccount]
  );

  useEffect(() => {
    const id = setTimeout(() => {
      void fetchWorker();
    }, 0);
    return () => clearTimeout(id);
  }, [fetchWorker]);

  const updatePassword = useCallback(
    async (password: string) => {
      if (!userId) return { success: false, error: 'Not authenticated' };
      setSaving(true);
      setError(null);
      try {
        const { error } = await supabase.auth.updateUser({ password });
        if (error) throw error;
        return { success: true };
      } catch {
        const message = 'Could not update password. Try logging out and using "Forgot password".';
        setError(message);
        return { success: false, error: message };
      } finally {
        setSaving(false);
      }
    },
    [userId]
  );

  return { worker, loading, error, saving, updateAccount, uploadPhoto, refresh: fetchWorker, updatePassword };
}
